/**
 * Playbooks API Service
 *
 * Handles all API calls related to the Playbooks feature.
 * Tenant-scoped; mirrors spacesService.ts / propensityService.ts patterns.
 */

import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import type {
  CreatePlaybookRequest,
  CreatePlaybookResponse,
  CreatePlaybookScheduleRequest,
  DraftPlaybookGoalResponse,
  ListPlaybookRunsResponse,
  ManualRunRequest,
  ManualRunResponse,
  Playbook,
  PlaybookRun,
  PlaybookSchedule,
  PlaybookSummary,
  TemplateValidationResult,
  TriggerRunRequest,
  TriggerRunResponse,
  UpdatePlaybookRequest,
  UpdatePlaybookScheduleRequest,
  XlsxWorkbookPreview,
} from '../types/playbooks';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function requireTenantPrefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return tenantApiPrefix(tid);
}

function extractErrorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') return fallback;
  const { detail } = body as Record<string, unknown>;
  if (!detail) return fallback;
  // Plain string detail
  if (typeof detail === 'string') return detail;
  // FastAPI validation errors: array of { loc, msg, type } objects
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === 'object' && d !== null ? (d as any).msg ?? JSON.stringify(d) : String(d)))
      .join('; ') || fallback;
  }
  return fallback;
}

async function readJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(extractErrorMessage(body, fallbackMessage));
  }
  return response.json();
}

// ─── Goal drafting ────────────────────────────────────────────────────────────

export async function draftPlaybookGoal(threadId: string): Promise<DraftPlaybookGoalResponse> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/threads/${threadId}/draft-playbook-goal`),
    { method: 'POST', headers: getAuthHeaders() },
  );
  return readJson<DraftPlaybookGoalResponse>(response, 'Failed to draft agent goal');
}

// ─── Playbook CRUD ────────────────────────────────────────────────────────────

export async function createPlaybook(payload: CreatePlaybookRequest): Promise<CreatePlaybookResponse> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks`),
    { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) },
  );
  return readJson<CreatePlaybookResponse>(response, 'Failed to create agent');
}

export async function listPlaybooks(params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<PlaybookSummary[]> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));
  const qs = query.toString() ? `?${query}` : '';
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks${qs}`),
    { method: 'GET', headers: getAuthHeaders() },
  );
  return readJson<PlaybookSummary[]>(response, 'Failed to list agents');
}

export async function getPlaybook(playbookId: string): Promise<Playbook> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}`),
    { method: 'GET', headers: getAuthHeaders() },
  );
  return readJson<Playbook>(response, 'Failed to load agent');
}

export async function updatePlaybook(playbookId: string, payload: UpdatePlaybookRequest): Promise<Playbook> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}`),
    { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(payload) },
  );
  return readJson<Playbook>(response, 'Failed to update agent');
}

export async function deletePlaybook(playbookId: string): Promise<void> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}`),
    { method: 'DELETE', headers: getAuthHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Failed to delete agent' }));
    throw new Error(err.detail || 'Failed to delete agent');
  }
}

// ─── Runs ─────────────────────────────────────────────────────────────────────

export async function triggerPlaybookRun(
  playbookId: string,
  payload?: TriggerRunRequest,
): Promise<TriggerRunResponse> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}/runs`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload ?? {}),
    },
  );
  return readJson<TriggerRunResponse>(response, 'Failed to trigger agent run');
}

/** Admin escape hatch: triggers a hardcoded manual report pipeline directly
 *  (bypasses the autonomous agent), for agents with `has_manual_run` set.
 *  Returns immediately with a 'running' run; poll listPlaybookRuns for it to
 *  flip to completed/failed. */
export async function triggerManualPlaybookRun(
  playbookId: string,
  payload: ManualRunRequest,
): Promise<ManualRunResponse> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}/manual-run`),
    { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) },
  );
  return readJson<ManualRunResponse>(response, 'Failed to start manual run');
}

export async function listPlaybookRuns(
  playbookId: string,
  params?: { limit?: number; offset?: number; status?: string },
): Promise<ListPlaybookRunsResponse> {
  const query = new URLSearchParams();
  if (params?.limit != null) query.set('limit', String(params.limit));
  if (params?.offset != null) query.set('offset', String(params.offset));
  if (params?.status) query.set('status', params.status);
  const qs = query.toString() ? `?${query}` : '';
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}/runs${qs}`),
    { method: 'GET', headers: getAuthHeaders() },
  );
  return readJson<ListPlaybookRunsResponse>(response, 'Failed to list agent runs');
}

export async function getPlaybookRun(playbookId: string, runId: string): Promise<PlaybookRun> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}/runs/${runId}`),
    { method: 'GET', headers: getAuthHeaders() },
  );
  return readJson<PlaybookRun>(response, 'Failed to load agent run');
}

/** Server-rendered, read-only preview of a run's filled workbook output. */
export async function getRunXlsxPreview(
  playbookId: string,
  runId: string,
  artifactId?: string,
): Promise<XlsxWorkbookPreview> {
  const qs = artifactId ? `?artifact_id=${encodeURIComponent(artifactId)}` : '';
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}/runs/${runId}/xlsx-preview${qs}`),
    { method: 'GET', headers: getAuthHeaders() },
  );
  return readJson<XlsxWorkbookPreview>(response, 'Failed to load workbook preview');
}

// ─── Excel output template ─────────────────────────────────────────────────────

/**
 * Validate a template file WITHOUT creating or modifying anything. Used to give
 * inline feedback the moment a user selects a template, so a bad file is caught
 * before an agent is created.
 */
export async function validatePlaybookTemplate(file: File): Promise<TemplateValidationResult> {
  const token = localStorage.getItem('klaris_jwt');
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/template/validate`),
    {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    },
  );
  return readJson<TemplateValidationResult>(response, 'Failed to validate template');
}

export async function uploadPlaybookTemplate(playbookId: string, file: File): Promise<Playbook> {
  const token = localStorage.getItem('klaris_jwt');
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}/template`),
    {
      method: 'POST',
      // NOTE: do not set Content-Type — the browser sets the multipart boundary.
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    },
  );
  return readJson<Playbook>(response, 'Failed to upload template');
}

export async function deletePlaybookTemplate(playbookId: string): Promise<Playbook> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}/template`),
    { method: 'DELETE', headers: getAuthHeaders() },
  );
  return readJson<Playbook>(response, 'Failed to remove template');
}

// ─── Schedules ──────────────────────────────────────────────────────────────
// NOTE: schedules are persisted and managed here but not yet executed — the
// worker/email delivery is deferred (see backend playbook_schedule model).

export async function listPlaybookSchedules(playbookId: string): Promise<PlaybookSchedule[]> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}/schedules`),
    { method: 'GET', headers: getAuthHeaders() },
  );
  return readJson<PlaybookSchedule[]>(response, 'Failed to load schedules');
}

export async function createPlaybookSchedule(
  playbookId: string,
  payload: CreatePlaybookScheduleRequest,
): Promise<PlaybookSchedule> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}/schedules`),
    { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) },
  );
  return readJson<PlaybookSchedule>(response, 'Failed to create schedule');
}

export async function updatePlaybookSchedule(
  playbookId: string,
  scheduleId: string,
  payload: UpdatePlaybookScheduleRequest,
): Promise<PlaybookSchedule> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}/schedules/${scheduleId}`),
    { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(payload) },
  );
  return readJson<PlaybookSchedule>(response, 'Failed to update schedule');
}

export async function deletePlaybookSchedule(playbookId: string, scheduleId: string): Promise<void> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}/schedules/${scheduleId}`),
    { method: 'DELETE', headers: getAuthHeaders() },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Failed to delete schedule' }));
    throw new Error((err as { detail?: string }).detail || 'Failed to delete schedule');
  }
}

export async function sendRunEmail(
  playbookId: string,
  runId: string,
  recipientEmails: string[],
): Promise<{ ok: boolean; message: string }> {
  const response = await fetch(
    buildApiUrl(`${requireTenantPrefix()}/playbooks/${playbookId}/runs/${runId}/send-email`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ recipient_emails: recipientEmails }),
    },
  );
  return readJson<{ ok: boolean; message: string }>(response, 'Failed to send email');
}

/** Returns the URL to download a run output. Defaults to the Word document;
 *  pass an artifactId to download a specific output (e.g. the filled Excel). */
export function getDocumentDownloadUrl(playbookId: string, runId: string, artifactId?: string): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  const qs = artifactId ? `?artifact_id=${encodeURIComponent(artifactId)}` : '';
  return buildApiUrl(`${tenantApiPrefix(tid)}/playbooks/${playbookId}/runs/${runId}/document${qs}`);
}

/** Returns the URL to download the agent's uploaded Excel template. */
export function getTemplateDownloadUrl(playbookId: string): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return buildApiUrl(`${tenantApiPrefix(tid)}/playbooks/${playbookId}/template`);
}
